import { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  CheckOutline,
  CloseCircleOutline,
  DropdownInput,
  DropdownOption,
  ManualWalletAdjustmentRequest,
  TransactionAccount,
  TransactionCategory,
  useToast,
} from '@aslanpay/portal-common-lib';

import CurrencyAmount from 'app/components/UI/CurrencyAmount/CurrencyAmount';
import MoneyAmountInput from 'app/components/UI/Inputs/MoneyAmountInput/MoneyAmountInput';

import {
  SystemOnlyTransactionCategories,
  TransactionAccountLabels,
  TransactionCategoryLabels,
  TransactionReasonLabels,
} from 'app/constants/transactions';

import useCreateManualAdjustment from 'app/hooks/useCreateManualAdjustment';

import { manualWalletAdjustmentSchema } from 'app/utils/schemas';

import {
  AdjustBalanceFormFields,
  AdjustBalanceModalProps,
  getDefaultAdjustBalanceFormFields,
} from './AdjustBalanceModal.types';

const DEPRECATED_CATEGORIES: TransactionCategory[] = ['DEPOSIT', 'WITHDRAW', 'FX_FEE'];

const categoryOptions: DropdownOption[] = Object.entries(TransactionCategoryLabels)
  .filter(
    ([value]) =>
      !DEPRECATED_CATEGORIES.includes(value as TransactionCategory) &&
      !SystemOnlyTransactionCategories.includes(value as TransactionCategory)
  )
  .map(([value, text]) => ({ value, text }));

const reasonOptions: DropdownOption[] = Object.entries(TransactionReasonLabels).map(
  ([value, text]) => ({ value, text })
);

const ALL_TRANSACTION_ACCOUNTS: TransactionAccount[] = [
  'REFUND',
  'CHARGEBACK',
  'CASHBACK',
  'RESERVE',
  'SALARY',
];

const AMOUNT_INPUT_WIDTH = '140px';
const ADJUSTMENT_GRID_COLUMNS = 'minmax(130px, 1fr) 120px 140px 120px';

const getDirectionSign = (direction?: AdjustBalanceFormFields['transactionDirection']) => {
  if (direction === 'DEBIT') return -1;
  if (direction === 'CREDIT') return 1;
  return 0;
};

function AdjustBalanceModal({
  isOpen,
  onClose,
  wallet,
  individualName,
  onSuccess = () => {},
}: AdjustBalanceModalProps) {
  const toast = useToast();

  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [pendingRequest, setPendingRequest] = useState<ManualWalletAdjustmentRequest | null>(null);

  const defaultValues = useMemo(
    () => getDefaultAdjustBalanceFormFields(ALL_TRANSACTION_ACCOUNTS),
    []
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<AdjustBalanceFormFields>({
    resolver: yupResolver(manualWalletAdjustmentSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { mutate: createManualAdjustment, isLoading: isSubmitting } = useCreateManualAdjustment();

  useEffect(() => {
    if (isOpen) {
      setIdempotencyKey(crypto.randomUUID());
      reset(defaultValues);
      setPendingRequest(null);
    }
  }, [isOpen, reset, defaultValues]);

  const handleClose = () => {
    setPendingRequest(null);
    onClose();
  };

  const watchedDirection = watch('transactionDirection');
  const watchedChildTransactions = watch('childTransactions');

  const sign = getDirectionSign(watchedDirection);
  const directionColor =
    // eslint-disable-next-line no-nested-ternary
    watchedDirection === 'DEBIT'
      ? 'system.error'
      : watchedDirection === 'CREDIT'
      ? 'system.success'
      : undefined;

  const totalAmount = watchedChildTransactions.reduce(
    (acc, childTransaction) => acc + (parseFloat(childTransaction?.amount || '0') || 0),
    0
  );

  const getChildAmount = (account: TransactionAccount) =>
    parseFloat(
      watchedChildTransactions.find(
        (childTransaction) => childTransaction.transactionAccount === account
      )?.amount || '0'
    ) || 0;

  const accountBalances: Partial<Record<TransactionAccount, number | undefined>> = {
    REFUND: wallet?.refundBalance,
    CHARGEBACK: wallet?.chargebackBalance,
    CASHBACK: wallet?.cashbackBalance,
    RESERVE: wallet?.reserveBalance,
    SALARY: wallet?.salaryBalance,
  };

  const adjustmentRows = ALL_TRANSACTION_ACCOUNTS.map((account) => {
    const amount = getChildAmount(account);

    return {
      account,
      amount,
      current: accountBalances[account] || 0,
      label: TransactionAccountLabels[account],
    };
  });

  const submitRequest = (request: ManualWalletAdjustmentRequest) => {
    createManualAdjustment(
      { walletId: request.walletId, request },
      {
        onSuccess: () => {
          toast({
            position: 'top-right',
            status: 'success',
            title: 'Balance adjusted successfully!',
          });
          setPendingRequest(null);
          onSuccess();
          onClose();
        },
        onError: () => {
          toast({
            position: 'top-right',
            status: 'error',
            title: 'Something went wrong',
            description: 'Sorry, the balance adjustment failed. Please try again.',
          });
        },
      }
    );
  };

  const handleConfirm = (data: AdjustBalanceFormFields) => {
    if (
      !wallet?.id ||
      !wallet?.currency ||
      !data.transactionDirection ||
      !data.transactionCategory ||
      !data.transactionReason ||
      !data.notes?.trim()
    ) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Unable to adjust balance',
        description: 'Wallet details or required adjustment fields are missing.',
      });
      return;
    }

    const request: ManualWalletAdjustmentRequest = {
      walletId: wallet.id,
      idempotencyKey,
      reference: (data.reference || '').trim(),
      transactionDirection: data.transactionDirection,
      transactionCategory: data.transactionCategory,
      transactionReason: data.transactionReason,
      notes: data.notes.trim(),
      amount: totalAmount,
      currency: wallet.currency,
      childTransactions: data.childTransactions
        .filter((childTransaction) => parseFloat(childTransaction.amount || '0') > 0)
        .map((childTransaction) => ({
          transactionAccount: childTransaction.transactionAccount,
          amount: parseFloat(childTransaction.amount),
        })),
    };

    setPendingRequest(request);
  };

  const pendingSign = getDirectionSign(pendingRequest?.transactionDirection);

  return (
    <>
      <Modal
        isOpen={isOpen}
        closeOnEsc={!isSubmitting}
        closeOnOverlayClick={!isSubmitting}
        onClose={handleClose}
        size="3xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="calc(100vh - 32px)">
          <ModalHeader>
            <HStack>
              <Text as="h6">Adjust balance{individualName ? ` – ${individualName}` : ''}</Text>
              <Spacer />
              <IconButton
                variant="link"
                icon={<CloseCircleOutline boxSize="30px" />}
                aria-label="Close adjust balance modal"
                onClick={handleClose}
                isDisabled={isSubmitting}
              />
            </HStack>
          </ModalHeader>
          <ModalBody>
            <form id="adjust-balance-form" onSubmit={handleSubmit(handleConfirm)} noValidate>
              <Stack spacing="14px">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                  <FormControl isInvalid={!!errors?.transactionCategory} isRequired>
                    <FormLabel>Transaction category</FormLabel>
                    <Controller
                      control={control}
                      name="transactionCategory"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          placeholder="Select a category"
                          defaultValue={categoryOptions.find((option) => option.value === value)}
                          options={categoryOptions}
                          onChange={(option) =>
                            onChange(option != null ? String(option) : undefined)
                          }
                          onBlur={onBlur}
                          isSearchable
                        />
                      )}
                    />
                    <FormErrorMessage>{errors?.transactionCategory?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors?.transactionReason} isRequired>
                    <FormLabel>Reason</FormLabel>
                    <Controller
                      control={control}
                      name="transactionReason"
                      render={({ field: { onChange, onBlur, value } }) => (
                        <DropdownInput
                          placeholder="Select a reason"
                          defaultValue={reasonOptions.find((option) => option.value === value)}
                          options={reasonOptions}
                          onChange={(option) =>
                            onChange(option != null ? String(option) : undefined)
                          }
                          onBlur={onBlur}
                        />
                      )}
                    />
                    <FormErrorMessage>{errors?.transactionReason?.message}</FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                <FormControl
                  isInvalid={
                    !!errors?.transactionDirection ||
                    typeof errors?.childTransactions?.message === 'string'
                  }
                  isRequired
                >
                  <Stack
                    direction={{ base: 'column', md: 'row' }}
                    justify="space-between"
                    align={{ base: 'stretch', md: 'flex-start' }}
                    mb="8px"
                    spacing="8px"
                  >
                    <Box>
                      <FormLabel mb="0">Adjustment</FormLabel>
                    </Box>

                    <Controller
                      control={control}
                      name="transactionDirection"
                      render={({ field: { onChange, value } }) => (
                        <HStack
                          spacing="2px"
                          p="2px"
                          bgColor="brand.lightGray2"
                          borderRadius="6px"
                          border="1px solid"
                          borderColor="brand.lightGray1"
                          alignSelf={{ base: 'flex-start', md: 'auto' }}
                        >
                          <Button
                            type="button"
                            size="xs"
                            px="10px"
                            minW="74px"
                            variant={value === 'CREDIT' ? 'solid' : 'ghost'}
                            colorScheme={value === 'CREDIT' ? 'green' : 'gray'}
                            onClick={() => onChange('CREDIT')}
                          >
                            Credit (+)
                          </Button>
                          <Button
                            type="button"
                            size="xs"
                            px="10px"
                            minW="74px"
                            variant={value === 'DEBIT' ? 'solid' : 'ghost'}
                            colorScheme={value === 'DEBIT' ? 'red' : 'gray'}
                            onClick={() => onChange('DEBIT')}
                          >
                            Debit (-)
                          </Button>
                        </HStack>
                      )}
                    />
                  </Stack>

                  <Box
                    display={{ base: 'none', md: 'grid' }}
                    gridTemplateColumns={ADJUSTMENT_GRID_COLUMNS}
                    columnGap="8px"
                    px="10px"
                    pb="6px"
                  >
                    <Text textStyle="extraSmallBold" color="brand.darkGray">
                      Account
                    </Text>
                    <Text textStyle="extraSmallBold" color="brand.darkGray" textAlign="right">
                      Current
                    </Text>
                    <Text textStyle="extraSmallBold" color="brand.darkGray" textAlign="right">
                      Amount
                    </Text>
                    <Text textStyle="extraSmallBold" color="brand.darkGray" textAlign="right">
                      Result
                    </Text>
                  </Box>

                  <VStack align="stretch" spacing="4px" data-testid="wallet-balance-preview">
                    {adjustmentRows.map(({ account, amount, current, label }, index) => (
                      <Box
                        key={account}
                        display={{ base: 'block', md: 'grid' }}
                        gridTemplateColumns={ADJUSTMENT_GRID_COLUMNS}
                        columnGap="8px"
                        alignItems="center"
                        p="8px 10px"
                        bgColor="brand.lightGray2"
                        borderRadius="8px"
                        border="1px solid"
                        borderColor="brand.lightGray1"
                        data-testid={`child-transaction-row-${account}`}
                      >
                        <Text textStyle="smallMedium">{label}</Text>
                        <CurrencyAmount
                          amount={current}
                          currency={wallet?.currency}
                          textStyle="extraSmallRegular"
                          fontFamily="Inconsolata, monospace"
                          color="brand.darkGray"
                          textAlign="right"
                        />
                        <Box
                          width={{ base: '100%', md: AMOUNT_INPUT_WIDTH }}
                          mt={{ base: '6px', md: 0 }}
                        >
                          <Controller
                            control={control}
                            name={`childTransactions.${index}.amount`}
                            render={({ field: { onChange, value } }) => (
                              <MoneyAmountInput
                                value={value}
                                onChange={onChange}
                                currency={wallet?.currency}
                                size="sm"
                                textAlign="right"
                                fontFamily="Inconsolata, monospace"
                                placeholder="0.00"
                                isInvalid={!!errors?.childTransactions?.[index]?.amount}
                                clearAriaLabel={`Clear ${label} amount`}
                              />
                            )}
                          />
                        </Box>
                        <CurrencyAmount
                          amount={current + sign * amount}
                          currency={wallet?.currency}
                          textStyle="extraSmallBold"
                          fontFamily="Inconsolata, monospace"
                          color={watchedDirection && amount > 0 ? directionColor : undefined}
                          textAlign="right"
                        />
                      </Box>
                    ))}
                    <Box
                      display={{ base: 'block', md: 'grid' }}
                      gridTemplateColumns={ADJUSTMENT_GRID_COLUMNS}
                      columnGap="8px"
                      alignItems="center"
                      p="8px 10px"
                      borderTop="1px solid"
                      borderColor="brand.lightGray1"
                      data-testid="wallet-balance-result"
                    >
                      <Text textStyle="smallBold">Wallet balance preview</Text>
                      <CurrencyAmount
                        amount={wallet?.balance || 0}
                        currency={wallet?.currency}
                        textStyle="extraSmallRegular"
                        fontFamily="Inconsolata, monospace"
                        color="brand.darkGray"
                        textAlign="right"
                      />
                      <CurrencyAmount
                        data-testid="child-transactions-total"
                        amount={totalAmount}
                        currency={wallet?.currency}
                        direction={watchedDirection}
                        showSign={!!watchedDirection}
                        textStyle="smallBold"
                        fontFamily="Inconsolata, monospace"
                        color={directionColor}
                        textAlign="right"
                      />
                      <CurrencyAmount
                        amount={(wallet?.balance || 0) + sign * totalAmount}
                        currency={wallet?.currency}
                        textStyle="smallBold"
                        fontFamily="Inconsolata, monospace"
                        color={watchedDirection && totalAmount > 0 ? directionColor : undefined}
                        textAlign="right"
                      />
                    </Box>
                  </VStack>
                  {typeof errors?.childTransactions?.message === 'string' && (
                    <Text textStyle="extraSmallMedium" color="system.error" mt="4px">
                      {errors.childTransactions.message}
                    </Text>
                  )}
                  <FormErrorMessage>{errors?.transactionDirection?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors?.reference} isRequired>
                  <FormLabel>Reference</FormLabel>
                  <Input
                    placeholder="Support ticket, fraud case, Jira key, or other audit reference"
                    {...register('reference')}
                  />
                  <FormErrorMessage>{errors?.reference?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors?.notes} isRequired>
                  <FormLabel>User-facing notes</FormLabel>
                  <Text textStyle="extraSmallRegular" color="brand.darkGray" mb="6px">
                    This text will be shown to the user for this wallet adjustment.
                  </Text>
                  <Textarea
                    placeholder="Message shown to the user"
                    rows={2}
                    minH="64px"
                    maxLength={100}
                    {...register('notes')}
                  />
                  <FormErrorMessage>{errors?.notes?.message}</FormErrorMessage>
                </FormControl>
              </Stack>
            </form>
          </ModalBody>
          <ModalFooter>
            <Text color="system.error" textStyle="extraSmallMedium">
              * mandatory
            </Text>
            <Spacer />
            <Button variant="outline" size="sm" onClick={handleClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              form="adjust-balance-form"
              type="submit"
              size="sm"
              rightIcon={<CheckOutline boxSize="20px" />}
              isLoading={isSubmitting}
              isDisabled={!isValid}
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={!!pendingRequest}
        closeOnEsc={!isSubmitting}
        closeOnOverlayClick={!isSubmitting}
        onClose={() => setPendingRequest(null)}
        size="md"
        isCentered
      >
        <ModalOverlay />
        <ModalContent data-testid="adjust-balance-confirmation-modal">
          <ModalHeader>
            <HStack>
              <Text as="h6">Confirm balance adjustment</Text>
              <Spacer />
              <IconButton
                variant="link"
                icon={<CloseCircleOutline boxSize="30px" />}
                aria-label="Close balance adjustment confirmation"
                onClick={() => setPendingRequest(null)}
                isDisabled={isSubmitting}
              />
            </HStack>
          </ModalHeader>
          <ModalBody>
            {pendingRequest && (
              <Stack spacing="14px">
                <HStack justify="space-between">
                  <Text textStyle="smallMedium" color="brand.darkGray">
                    Direction
                  </Text>
                  <Text textStyle="smallBold">{pendingRequest.transactionDirection}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text textStyle="smallMedium" color="brand.darkGray">
                    Amount
                  </Text>
                  <CurrencyAmount
                    amount={pendingRequest.amount}
                    currency={pendingRequest.currency}
                    direction={pendingRequest.transactionDirection}
                    showSign
                    textStyle="smallBold"
                    color={
                      pendingRequest.transactionDirection === 'DEBIT'
                        ? 'system.error'
                        : 'system.success'
                    }
                  />
                </HStack>
                <HStack justify="space-between">
                  <Text textStyle="smallMedium" color="brand.darkGray">
                    Wallet balance
                  </Text>
                  <HStack spacing="8px">
                    <CurrencyAmount
                      amount={wallet?.balance || 0}
                      currency={pendingRequest.currency}
                      textStyle="extraSmallRegular"
                      color="brand.darkGray"
                    />
                    <Text textStyle="extraSmallRegular" color="brand.darkGray">
                      →
                    </Text>
                    <CurrencyAmount
                      amount={(wallet?.balance || 0) + pendingSign * pendingRequest.amount}
                      currency={pendingRequest.currency}
                      textStyle="smallBold"
                      color={
                        pendingRequest.transactionDirection === 'DEBIT'
                          ? 'system.error'
                          : 'system.success'
                      }
                    />
                  </HStack>
                </HStack>
                <HStack justify="space-between">
                  <Text textStyle="smallMedium" color="brand.darkGray">
                    Reference
                  </Text>
                  <Text textStyle="smallBold">{pendingRequest.reference}</Text>
                </HStack>
                <Box>
                  <Text textStyle="smallMedium" color="brand.darkGray" mb="6px">
                    User-facing notes
                  </Text>
                  <Box
                    p="10px"
                    bgColor="brand.lightGray2"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="brand.lightGray1"
                  >
                    <Text textStyle="smallRegular">{pendingRequest.notes}</Text>
                  </Box>
                </Box>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPendingRequest(null)}
              isDisabled={isSubmitting}
            >
              Back
            </Button>
            <Button
              size="sm"
              rightIcon={<CheckOutline boxSize="20px" />}
              isLoading={isSubmitting}
              isDisabled={!pendingRequest}
              onClick={() => {
                if (pendingRequest) {
                  submitRequest(pendingRequest);
                }
              }}
            >
              Submit adjustment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default AdjustBalanceModal;
